interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
  className?: string;
}

export default function Pagination({
  page,
  pageSize,
  total,
  onChange,
  className = '',
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const from = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, total);

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter((p) => {
      if (totalPages <= 7) return true;
      if (p === 1 || p === totalPages) return true;
      if (Math.abs(p - safePage) <= 1) return true;
      return false;
    })
    .reduce<(number | 'ellipsis')[]>((acc, p, i, arr) => {
      if (i > 0) {
        const prev = arr[i - 1];
        if (p - prev > 1) acc.push('ellipsis');
      }
      acc.push(p);
      return acc;
    }, []);

  return (
    <div className={`flex items-center justify-between gap-3 ${className}`}>
      <span className="text-xs text-foreground-500 whitespace-nowrap">
        显示第 {from}-{to} 条，共 {total} 条
      </span>
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onChange(1)}
            disabled={safePage === 1}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-background-200 text-foreground-400 hover:border-primary-300 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <i className="ri-skip-left-line text-sm" />
          </button>
          <button
            type="button"
            onClick={() => onChange(Math.max(1, safePage - 1))}
            disabled={safePage === 1}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-background-200 text-foreground-500 hover:border-primary-300 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <i className="ri-arrow-left-s-line" />
          </button>

          {pages.map((item, i) =>
            item === 'ellipsis' ? (
              <span
                key={`ellipsis-${i}`}
                className="w-8 h-8 flex items-center justify-center text-xs text-foreground-400"
              >
                ...
              </span>
            ) : (
              <button
                key={item}
                type="button"
                onClick={() => onChange(item)}
                className={`w-8 h-8 flex items-center justify-center rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  safePage === item
                    ? 'bg-primary-500 text-background-50'
                    : 'border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                {item}
              </button>
            ),
          )}

          <button
            type="button"
            onClick={() => onChange(Math.min(totalPages, safePage + 1))}
            disabled={safePage === totalPages}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-background-200 text-foreground-500 hover:border-primary-300 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
          <button
            type="button"
            onClick={() => onChange(totalPages)}
            disabled={safePage === totalPages}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-background-200 text-foreground-400 hover:border-primary-300 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <i className="ri-skip-right-line text-sm" />
          </button>
        </div>
      )}
    </div>
  );
}
